/**
 * Representation Page Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive representation page with advanced features:
 * - Interactive stakeholder group visualization
 * - Dynamic pie chart data for representation statistics
 * - Year-based data filtering and navigation
 * - Modal table displays for detailed group information
 * - Diagram data visualization for domestic, EU, and international representation
 * - Tooltip integration for enhanced user experience
 * - Interactive group selection and expansion
 * - Color-coded sectional mapping
 * 
 * Features:
 * - Real-time data fetching for diagram, pie, and table data
 * - Dynamic year selection with next/previous navigation
 * - Interactive doughnut charts with Chart.js integration
 * - Expandable table modal with detailed group information
 * - HTML content parsing for group descriptions
 * - Image mapping for sectional icons
 * - Responsive design for various screen sizes
 * - Loading states and error handling
 * 
 * @returns {JSX.Element} Representation page component with comprehensive stakeholder visualization
 */
import React, { useState, useEffect } from "react";
import Divider from "../components/Common/Divider";
import "../css/Colors.css";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { Doughnut } from "react-chartjs-2";
import apiHelper from "../utils/apiHelper";
import LoadingSpinner from "../components/Common/LoadingSpinner";

import "./Representation.css";

/**
 * Representation component for displaying stakeholder groups
 * and representation statistics with interactive charts
 */
function Representation() {
  const [diagramData, setDiagramData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [allAvailalbeYears, setAllAvailableYears] = useState([]);
  const [tableState, setTableState] = useState({
    isOpen: false,
    id: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDataDiagram = async () => {
      try {
        const res = await apiHelper.fetchJSONData("representations/diagram");
        setDiagramData(res);
      } catch (err) {
        console.error('Error fetching diagram data:', err);
      }
    };

    const fetchDataPie = async () => {
      try {
        const res = await apiHelper.fetchJSONData("representations/pie");
        // identify most recent year
        let mostRecentYear = "";
        let availableYears = [];
        for (let i = 0; i < res.length; i++) {
          let obj = res[i];
          let fields = Object.keys(obj);
          for (let j = 0; j < fields.length; j++) {
            let field = fields[j];
            if (field.startsWith("year_")) {
              let year = parseInt(field.replace("year_", ""));
              if (!mostRecentYear || mostRecentYear < year) {
                mostRecentYear = year;
              }

              if (availableYears.indexOf(year) < 0) {
                availableYears.push(year);
              }
            }
          }
        }

        setAllAvailableYears(availableYears);
        setSelectedYear(mostRecentYear);
        setPieData(res);

        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching pie data:', err);
      }
    };

    const fetchDataTable = async () => {
      try {
        const res = await apiHelper.fetchJSONData("representations/table");
        setTableData(res);
      } catch (err) {
        console.error('Error fetching table data:', err);
      }
    };

    fetchDataTable();
    fetchDataDiagram();
    fetchDataPie();
  }, []);

  const handleClose = () => {
    setTableState({ isOpen: false, id: [] });
  };

  const handlePrev = () => {
    let nextYear = selectedYear - 1;
    if (allAvailalbeYears.indexOf(nextYear) < 0) {
      nextYear = allAvailalbeYears[allAvailalbeYears.length - 1];
    }
    setSelectedYear(nextYear);
  };
  const handleNext = () => {
    let nextYear = selectedYear + 1;
    if (allAvailalbeYears.indexOf(nextYear) < 0) {
      nextYear = allAvailalbeYears[0];
    }
    setSelectedYear(nextYear);
  };

  function extractLinesFromHTML(htmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
  
    const allElements = Array.from(doc.querySelectorAll("div, span, p"));
  
    const lines = allElements
      .filter(el => el.children.length === 0)
      .map(el => el.textContent.trim())
      .filter(text => text.length > 0);
  
    return [...new Set(lines)];
  }

  const imageMapping = {
    Domestic: "/assets/img/representation/domestic.svg",
    EU: "/assets/img/representation/eu.svg",
    International: "/assets/img/representation/international.svg",
  };

  const backgroundColorMapping = {
    Domestic: "#377e22",
    EU: "#1614FD",
    International: "#006D92",
  };

  const backgroundColorMappingGroups = {
    Domestic: "#CBEAC4",
    EU: "#DCDFFA",
    International: "#D0EBF3",
  };

  const sections = ["Domestic", "EU", "International"];

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div className="container mt-10">
        <div className="r-intro">
          Welcome to the Representation Page, Irish Funds plays a vital role in representing the industry on a domestic, EU, and international basis. As the voice of the investment funds industry in Ireland, we work to promote and support the development of a world-class environment for funds and asset managers, ensuring Ireland is recognized globally as a leading jurisdiction for investment management.
        </div>
      </div>
      <Divider height={90} />
      <div className="bg-primary">
        <div className="container">
          <div className="left-line">
            <h2 className="text-uppercase main-header">
              Where Irish Funds Represents You:
            </h2>
            <h2 className="text-uppercase main-header h2-green">
              Maximising Your Reach - 36 Stakeholder Groups
            </h2>
          </div>

          <div className="container groups-container">
            {sections.map((section, index) => (
              <>
                <div className="bg-white relative-container">
                  <div
                    key={index}
                    className="section-container"
                    style={{ backgroundColor: backgroundColorMapping[section] }}
                  >
                    <img
                      src={imageMapping[section]}
                      alt={section}
                      className="section-image"
                    />
                    <h4 className="rep-section-title">{section}</h4>
                  </div>
                  <div className="container groups-container-list">
                    {diagramData
                      .filter((item) => item.Title === section)
                      .map((item, index) => (
                        <div
                          key={index}
                          className="group-item"
                          style={{
                            width: section !== "Domestic" ? "100%" : "",
                          }}
                        >
                          {item.ConveyorDesc_x0028_Tippy_x0029_ ? (
                            <Tippy
                              content={item.ConveyorDesc_x0028_Tippy_x0029_}
                              placement="bottom"
                            >
                              <div>
                                <div
                                  onClick={() => {
                                    setTableState({
                                      isOpen: true,
                                      id: tableData
                                        .filter(
                                          (tableItem) =>
                                            (tableItem.Conveyor ===
                                              item.Conveyor ||
                                              tableItem.Conveyor ===
                                                item.Conveyor0) &&
                                            tableItem.Region === section &&
                                            tableItem.PublishFlag
                                        )
                                        .map((tableItem) => tableItem.id),
                                    });
                                  }}
                                  className={`group-container ${section.toLowerCase()}-hover`}
                                >
                                  <h5 className="group-title">
                                    {item.Conveyor}
                                  </h5>
                                  <div
                                    className="group-color-block"
                                    style={{
                                      backgroundColor:
                                        backgroundColorMapping[section],
                                      width: 32,
                                      minWidth: 32,
                                      height: 58,
                                    }}
                                  ></div>
                                </div>
                              </div>
                            </Tippy>
                          ) : (
                            <div>
                              <div
                                onClick={() => {
                                  setTableState({
                                    isOpen: true,
                                    id: tableData
                                      .filter(
                                        (tableItem) =>
                                          (tableItem.Conveyor ===
                                            item.Conveyor ||
                                            tableItem.Conveyor ===
                                              item.Conveyor0) &&
                                          tableItem.Region === section &&
                                          tableItem.PublishFlag
                                      )
                                      .map((tableItem) => tableItem.id),
                                  });
                                }}
                                className={`group-container ${section.toLowerCase()}-hover`}
                              >
                                <h5 className="group-title">{item.Conveyor}</h5>
                                <div
                                  className="group-color-block"
                                  style={{
                                    backgroundColor:
                                      backgroundColorMapping[section],
                                    width: 32,
                                    minWidth: 32,
                                    height: 58,
                                  }}
                                ></div>
                              </div>
                            </div>
                          )}
                          <ul className="row-list">
                            {typeof item.ImageDescription === "string"
                              ? extractLinesFromHTML(item.ImageDescription).map(
                                  (line, index) => (
                                    <li key={index} className="list-item">
                                      {line}
                                    </li>
                                  )
                                )
                              : null}
                          </ul>
                        </div>
                      ))}
                  </div>
                  {false && (
                    <div className="view-forums-btn-container">
                      <div
                        className="view-forums-btn"
                        onClick={() => {
                          const allForums = tableData
                            .filter(
                              (item) =>
                                item.Region === section && item.PublishFlag
                            )
                            .map((item) => item.id);
                          setTableState({ isOpen: true, id: allForums });
                        }}
                      >
                        <div className="font-14">
                          <p>View all Forums</p>
                          <img
                            src={"/assets/img/representation/forums-icon.svg"}
                            alt={"View all Forums"}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {tableState.isOpen &&
                tableState.id.length > 0 &&
                tableState.id.some((id) => {
                  const matchedItem = tableData.find((item) => item.id === id);
                  return matchedItem && matchedItem.Region === section;
                }) ? (
                  <div className="mobile-only">
                    <div className="mt-5 container bg-white">
                      <div className="subject-table-cells">
                        {(() => {
                          const table = tableData.filter((item) =>
                            tableState.id.includes(item.id)
                          );

                          if (!table || table.length === 0) return null;

                          const showSubject = table.some(
                            (item) => item.Subject
                          );
                          const showFreq = table.some(
                            (item) => item.Frequency_x002f_Cadence
                          );
                          const showConveyor = table.some(
                            (item) => item.Conveyor
                          );
                          const showIFRep = table.some((item) => item.IFRep);
                          const showOrg = table.some(
                            (item) => item.Organisation
                          );

                          return (
                            <div className="responsive-table-wrapper">
                              <table className="rep-table">
                                <thead>
                                  <tr>
                                    {showSubject && (
                                      <th className="table-subject">
                                        <h5 className="table-subject-text">
                                          Subject
                                        </h5>
                                      </th>
                                    )}
                                    {showFreq && (
                                      <th className="table-subject">
                                        <h5 className="table-subject-text">
                                          Freq/ Cadence
                                        </h5>
                                      </th>
                                    )}
                                    {showConveyor && (
                                      <th className="table-subject">
                                        <h5 className="table-subject-text">
                                          Conveyor
                                        </h5>
                                      </th>
                                    )}
                                    {showIFRep && (
                                      <th className="table-subject">
                                        <h5 className="table-subject-text">
                                          IF Rep
                                        </h5>
                                      </th>
                                    )}
                                    {showOrg && (
                                      <th className="table-subject">
                                        <h5 className="table-subject-text">
                                          Organisation
                                        </h5>
                                      </th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {table.map((item, index) => (
                                    <tr key={`row-${index}`}>
                                      {showSubject && (
                                        <td className="table-items">
                                          <p>{item.Subject}</p>
                                        </td>
                                      )}
                                      {showFreq && (
                                        <td className="table-items">
                                          <p>{item.Frequency_x002f_Cadence}</p>
                                        </td>
                                      )}
                                      {showConveyor && (
                                        <td className="table-items">
                                          <p>{item.Conveyor}</p>
                                        </td>
                                      )}
                                      {showIFRep && (
                                        <td className="table-items">
                                          <p>{item.IFRep}</p>
                                        </td>
                                      )}
                                      {showOrg && (
                                        <td className="table-items">
                                          <p>{item.Organisation}</p>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="close-btn-container">
                        <div className="close-btn-spacer" />
                        <div onClick={handleClose} className="rep-close-btn">
                          <p>Close</p>
                          <img
                            src="/assets/img/representation/close.svg"
                            alt="Close"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            ))}
          </div>

          {tableState.isOpen && tableState.id.length > 0 ? (
            <div className="container table-open-container desktop-only">
              <div className="mt-5 container bg-white">
                <div className="subject-table-cells">
                  {(() => {
                    const table = tableData.filter((item) =>
                      tableState.id.includes(item.id)
                    );

                    if (!table || table.length === 0) return null;

                    const showSubject = table.some((item) => item.Subject);
                    const showFreq = table.some(
                      (item) => item.Frequency_x002f_Cadence
                    );
                    const showConveyor = table.some((item) => item.Conveyor);
                    const showIFRep = table.some((item) => item.IFRep);
                    const showOrg = table.some((item) => item.Organisation);

                    return (
                      <table className="rep-table">
                        <thead>
                          <tr>
                            {showSubject && (
                              <th className="table-subject">
                                <h5 className="table-subject-text">Subject</h5>
                              </th>
                            )}
                            {showFreq && (
                              <th className="table-subject">
                                <h5 className="table-subject-text">
                                  Freq/ Cadence
                                </h5>
                              </th>
                            )}
                            {showConveyor && (
                              <th className="table-subject">
                                <h5 className="table-subject-text">Conveyor</h5>
                              </th>
                            )}
                            {showIFRep && (
                              <th className="table-subject">
                                <h5 className="table-subject-text">IF Rep</h5>
                              </th>
                            )}
                            {showOrg && (
                              <th className="table-subject">
                                <h5 className="table-subject-text">
                                  Organisation
                                </h5>
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {table.map((item, index) => (
                            <tr key={`row-${index}`}>
                              {showSubject && (
                                <td className="table-items">
                                  <p>{item.Subject}</p>
                                </td>
                              )}
                              {showFreq && (
                                <td className="table-items">
                                  <p>{item.Frequency_x002f_Cadence}</p>
                                </td>
                              )}
                              {showConveyor && (
                                <td className="table-items">
                                  <p>{item.Conveyor}</p>
                                </td>
                              )}
                              {showIFRep && (
                                <td className="table-items">
                                  <p>{item.IFRep}</p>
                                </td>
                              )}
                              {showOrg && (
                                <td className="table-items">
                                  <p>{item.Organisation}</p>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
                <div className="close-btn-container">
                  <div className="close-btn-spacer" />
                  <div onClick={handleClose} className="rep-close-btn">
                    <p>Close</p>
                    <img
                      src="/assets/img/representation/close.svg"
                      alt="Close"
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="container bg-primary spacer-50-pt spacer-100-pb">
          <div className="left-line">
            <h4 className="text-uppercase main-header">Advocacy in Numbers:</h4>
            <h4 className="text-uppercase main-header h2-green">
              Stakeholders Meetings
            </h4>
          </div>

          <div className="container mt-5">
            <div className="bg-white chart-container">
              <div className="chart-legend-container">
                {pieData.map((item, index) => (
                  <div key={index} className="container legend-item">
                    <div style={{ backgroundColor: item.PieColour }} />
                    <p>{item.StakeholderMeeting}</p>
                  </div>
                ))}
              </div>

              <div className="chart-image">
                <img
                  src="/assets/img/representation/chart-image.png"
                  alt="chart-image"
                />
              </div>

              <div className="doughnut-container">
                <div className="doughnut-chart">
                  <Doughnut
                    data={{
                      labels: pieData.map((item) => item.StakeholderMeeting),
                      datasets: [
                        {
                          data: pieData.map(
                            (item) => item["year_" + selectedYear]
                          ),
                          backgroundColor: pieData.map(
                            (item) => item.PieColour
                          ),
                          borderAlign: "center",
                          spacing: 0,
                        },
                      ],
                    }}
                    options={{
                      type: "doughnut",
                      plugins: {
                        legend: { display: false },
                        datalabels: {
                          color: "black",
                          font: { weight: "700", size: 14 },
                        },
                        tooltip: {
                          callbacks: {
                            label: (tooltipItem) => " " + tooltipItem.label,
                            afterLabel: () => "",
                            title: () => "",
                          },
                        },
                      },
                    }}
                  />
                  <div className="doughnut-center-content">
                    <div className="image-wrapper">
                      <img
                        src="/assets/img/representation/chart-circle.svg"
                        alt="Center"
                      />
                      <span>
                        <h4 className="center-number">
                          {pieData.reduce(
                            (sum, item) => sum + (item["year_" + selectedYear] ?? 0),
                            0
                          )}
                        </h4>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="container chart-controls">
                  <div className="prev-btn" onClick={handlePrev}>
                    <img
                      src={"/assets/img/representation/arrow-left.svg"}
                      alt={"Close"}
                    />
                    <p>Previous</p>
                  </div>
                  <div className="current-year">
                    <p>{selectedYear}</p>
                  </div>
                  <div className="next-btn" onClick={handleNext}>
                    <p>Next</p>
                    <img
                      src={"/assets/img/representation/arrow-right.svg"}
                      alt={"Close"}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Representation;
